import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '@/components/landing/Header';

const TermsOfServicePage = () => {
  return (
    <>
      <Helmet>
        <title>Terms and Conditions - Your Song Request</title>
        <meta name="description" content="Terms and conditions for using the Your Song Request platform." />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-200">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-gray-800 rounded-lg shadow-xl p-8 md:p-12">
            <h1 className="text-4xl font-bold text-white mb-8">Terms and Conditions</h1>
            
            <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">1. Introduction</h2>
                <p className="leading-relaxed">
                  Welcome to Your Song Request (YSR). These Terms govern your use of our platform to interact with live musicians. By using our service, you agree to these terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">2. The Service</h2>
                <p className="leading-relaxed">
                  Your Song Request provides a platform for audience members (&ldquo;Users&rdquo;) to pay a priority fee to request songs from a Musician&apos;s verified repertoire. The fee is strictly for the service of prioritizing a request in a live setting. We do not sell digital audio files, MP3s, or copyrighted media.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">3. Artist Earnings and Withdrawals</h2>
                <p className="leading-relaxed mb-4">
                  Musicians (&ldquo;Artists&rdquo;) earn funds through User requests. To maintain platform sustainability and offset administrative and acquisition costs, the following withdrawal rules apply:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li className="leading-relaxed">
                    <strong>Initial Withdrawal:</strong> An Artist&apos;s first withdrawal is subject to a minimum threshold of £15 (UK/Ireland) or $20 (US).
                  </li>
                  <li className="leading-relaxed">
                    <strong>Subsequent Withdrawals:</strong> All following withdrawals are subject to a minimum threshold of £10 (UK/Ireland) or $10 (US).
                  </li>
                  <li className="leading-relaxed">
                    <strong>Account Closure:</strong> If an Artist chooses to permanently close their account, YSR will remit any remaining balance regardless of these thresholds, subject to a flat £5 / $5 administrative processing fee (not to exceed the total balance held).
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">4. Payments by Region</h2>
                <p className="leading-relaxed mb-4">
                  Payment processing depends on where you are using the service:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li className="leading-relaxed">
                    <strong>United Kingdom:</strong> For artists and end users, payments and card processing are provided via A2B and Cardstream (or as disclosed in-product), in pounds sterling (GBP).
                  </li>
                  <li className="leading-relaxed">
                    <strong>United States, Canada, Ireland, the Eurozone, Australia, and New Zealand:</strong> For artists and end users, payments are processed by PayPal (or as shown in-product) in the artist&apos;s local currency (USD, CAD, EUR, AUD, or NZD).
                  </li>
                  <li className="leading-relaxed">
                    <strong>General:</strong> Your Song Request acts as a commercial agent or intermediary for billing. Fees, chargebacks, and settlement timelines follow the processor and your bank.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">5. Taxes and Reporting</h2>
                <p className="leading-relaxed mb-4">
                  Artists are independent contractors and are solely responsible for all applicable taxes on earnings.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li className="leading-relaxed">
                    <strong>Reporting:</strong> Where required by law (e.g., HMRC &ldquo;Reporting Rules for Digital Platforms&rdquo; in the UK or IRS Form 1099-K in the US), YSR will collect tax identification information.
                  </li>
                  <li className="leading-relaxed">
                    <strong>Requirement:</strong> Artists may be required to provide a National Insurance number (UK) or Social Security Number/EIN (US) before funds are released if they exceed government-mandated reporting thresholds.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">6. Inactive Accounts and Unclaimed Property</h2>
                <p className="leading-relaxed mb-4">
                  If an account remains &ldquo;Inactive&rdquo; (no login activity or transactions) for a continuous period:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li className="leading-relaxed">
                    <strong>US Residents:</strong> After 36 months of inactivity, balances will be treated as unclaimed property and handled in accordance with state Escheatment Laws. YSR will attempt to contact the Artist via their registered email before transferring funds to the relevant state authority.
                  </li>
                  <li className="leading-relaxed">
                    <strong>UK/International Residents:</strong> After 6 years of inactivity, YSR reserves the right to close the account and treat any remaining balance as forfeited in accordance with the Limitation Act 1980.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">7. Availability &amp; Performance</h2>
                <p className="leading-relaxed mb-4">
                  Requests can only be made when a Musician has set their status to &lsquo;Live.&rsquo;
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li className="leading-relaxed">
                    <strong>Discretion:</strong> Payment of a priority fee does not guarantee a performance. Musicians reserve the right to decline any request.
                  </li>
                  <li className="leading-relaxed">
                    <strong>Refunds:</strong> If a request is declined or remains unfulfilled when a session ends, a refund or credit will be issued in accordance with our{' '}
                    <Link to="/refund-policy" className="text-blue-400 hover:text-blue-300 underline">
                      Refund Policy
                    </Link>
                    .
                  </li>
                  <li className="leading-relaxed">
                    <strong>Authenticity:</strong> All performances must be live and human-led. The use of unauthorized AI-generated voice clones or &ldquo;deepfake&rdquo; technology to impersonate third-party artists is prohibited.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">8. User Conduct</h2>
                <p className="leading-relaxed">
                  Users agree to use the platform only for lawful purposes. Any attempt to bypass the payment system or harass performers via the request text function will result in an immediate ban.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">9. Limitation of Liability</h2>
                <p className="leading-relaxed">
                  Your Song Request acts as the technology intermediary. While we ensure the delivery of the request signal to the Artist, the artistic performance of the song is the sole responsibility of the Musician.
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-700">
              <Link 
                to="/" 
                className="text-blue-400 hover:text-blue-300 underline text-sm"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-black border-t border-gray-800 py-8 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                © 2026 Your Song Request. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                <Link to="/refund-policy" className="hover:text-gray-400 transition-colors">
                  Refund Policy
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/terms-of-service" className="hover:text-gray-400 transition-colors">
                  Terms & Conditions
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/privacy-policy" className="hover:text-gray-400 transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-800 text-center">
              <p className="text-xs text-gray-600">
                Registered Business Address: 6 Pendle Close, Thornton-Cleveley's, FY5 3WB
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermsOfServicePage;
